import React, { useState } from 'react';
import {
    Modal,
    Tabs,
    Card,
    Typography,
    Row,
    Col,
    Timeline,
    Table,
    Space,
    Tag,
    Grid
} from 'antd';
import {
    DatabaseOutlined,
    BarChartOutlined,
    BookOutlined,
    BulbOutlined,
    FileTextOutlined,
    SettingOutlined,
    ThunderboltOutlined
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

interface AboutModalProps {
    visible: boolean;
    onCancel: () => void;
}

const AboutModal: React.FC<AboutModalProps> = ({ visible, onCancel }) => {
    const [activeTab, setActiveTab] = useState('overview');
    const { useBreakpoint } = Grid;
    const screens = useBreakpoint();

    const capabilities = [
        {
            category: "Data Processing",
            description: "Comprehensive support for standard single-cell data formats",
            technologies: ["10X Genomics", "AnnData (.h5ad)", "Spaceranger", "Custom matrices"]
        },
        {
            category: "Analysis Methods",
            description: "State-of-the-art computational algorithms for single-cell analysis",
            technologies: ["Quality control", "Normalization", "Dimensionality reduction", "Clustering", "Differential expression", "Pathway analysis"]
        },
        {
            category: "AI-Assisted Analysis",
            description: "Intelligent agent system for automated parameter optimization and interpretation",
            technologies: ["Parameter auto-filling", "Error debugging", "Result interpretation", "Method recommendation", "Workflow optimization"]
        },
        {
            category: "Visualization",
            description: "Interactive plotting and data exploration tools",
            technologies: ["UMAP/t-SNE embeddings", "Heatmaps", "Violin plots", "Feature plots", "3D visualization"]
        },
        {
            category: "Computational Resources",
            description: "Scalable cloud infrastructure for intensive analyses",
            technologies: ["Auto-scaling compute", "GPU acceleration", "Memory optimization", "Parallel processing"]
        }
    ];

    const technicalSpecs = [
        {
            key: '1',
            component: 'Data Input',
            specification: 'Multiple format support with automatic validation',
            details: '10X, H5AD, CSV, MTX formats'
        },
        {
            key: '2',
            component: 'Processing Engine',
            specification: 'Distributed computing with containerized environments',
            details: 'Docker, Kubernetes, Auto-scaling'
        },
        {
            key: '3',
            component: 'AI Agent System',
            specification: 'Large language model integration for intelligent assistance',
            details: 'Parameter optimization, Error debugging, Result interpretation'
        },
        {
            key: '4',
            component: 'Analysis Pipeline',
            specification: 'Modular workflow management system',
            details: 'Drag-drop interface, Template library'
        },
        {
            key: '5',
            component: 'Visualization',
            specification: 'Interactive plotting with export capabilities',
            details: 'WebGL rendering, SVG/PNG export'
        },
        {
            key: '6',
            component: 'Data Security',
            specification: 'Enterprise-grade encryption and access control',
            details: 'AES-256, Role-based permissions'
        }
    ];

    const columns = [
        {
            title: 'Component',
            dataIndex: 'component',
            key: 'component',
            width: screens.xs ? '100%' : '25%'
        },
        {
            title: 'Specification',
            dataIndex: 'specification',
            key: 'specification',
            width: screens.xs ? '100%' : '45%'
        },
        {
            title: 'Technical Details',
            dataIndex: 'details',
            key: 'details',
            width: screens.xs ? '100%' : '30%',
            render: (text: string) => <Text code style={{ fontSize: screens.xs ? 10 : 12 }}>{text}</Text>
        }
    ];

    return (
        <Modal
            open={visible}
            onCancel={onCancel}
            footer={null}
            width={screens.xs ? "100vw" : "90vw"}
            style={{
                maxWidth: screens.xs ? "100vw" : 1200,
                top: screens.xs ? 0 : 20,
                margin: screens.xs ? 0 : 'auto',
                padding: screens.xs ? 0 : 'auto',
                height: screens.xs ? '100vh' : 'auto',
            }}
            styles={{
                body: {
                    padding: 0,
                    height: screens.xs ? '100vh' : 'auto',
                    display: screens.xs ? 'flex' : 'block',
                    flexDirection: screens.xs ? 'column' : 'initial',
                    maxHeight: screens.xs ? '100vh' : '85vh',
                    overflow: 'auto',
                    borderRadius: screens.xs ? 0 : 16
                },
                content: {
                    height: screens.xs ? '100vh' : 'auto',
                    maxHeight: screens.xs ? '100vh' : '90vh',
                    borderRadius: screens.xs ? 0 : 16,
                    display: screens.xs ? 'flex' : 'block',
                    flexDirection: screens.xs ? 'column' : 'initial'
                },
                mask: screens.xs ? { display: 'none' } : {}
            }}
        >
            <div style={{ position: 'relative', overflow: 'hidden', height: screens.xs ? '100vh' : 'auto', display: screens.xs ? 'flex' : 'block', flexDirection: screens.xs ? 'column' : 'initial' }}>
                <div style={{
                    background: '#fff',
                    padding: screens.xs ? '24px 16px 16px' : '40px 40px 20px',
                    borderBottom: '2px solid #e8e8e8',
                    position: 'relative',
                    flexShrink: 0
                }}>
                    <div style={{ maxWidth: 800 }}>
                        <Title level={1} style={{
                            color: '#2c3e50',
                            marginBottom: 16,
                            fontSize: screens.xs ? 20 : 32,
                            fontWeight: 600,
                            lineHeight: 1.2
                        }}>
                            Ragomics: A Multi-Agent Platform for Scalable Single-Cell and Spatial Genomics
                        </Title>
                        <Paragraph style={{
                            color: '#5a6c7d',
                            fontSize: screens.xs ? 14 : 16,
                            lineHeight: 1.6,
                            marginBottom: 20,
                            maxWidth: 700
                        }}>
                            An integrated bioinformatics environment providing researchers with
                            accessible tools for advanced single-cell RNA sequencing, multi-omics,
                            and spatial transcriptomics analysis without requiring programming expertise.
                        </Paragraph>
                        <Space wrap>
                            <Tag color="#f0f0f0" style={{
                                color: '#666',
                                border: 'none',
                                padding: screens.xs ? '2px 8px' : '4px 12px',
                                fontSize: screens.xs ? 10 : 12
                            }}>
                                Scalable Data Processing
                            </Tag>
                            <Tag color="#f0f0f0" style={{
                                color: '#666',
                                border: 'none',
                                padding: screens.xs ? '2px 8px' : '4px 12px',
                                fontSize: screens.xs ? 10 : 12
                            }}>
                                No-Code Environment
                            </Tag>
                            <Tag color="#f0f0f0" style={{
                                color: '#666',
                                border: 'none',
                                padding: screens.xs ? '2px 8px' : '4px 12px',
                                fontSize: screens.xs ? 10 : 12
                            }}>
                                Agent Assisted Analysis
                            </Tag>
                        </Space>
                    </div>
                </div>

                <div style={{
                    padding: screens.xs ? '0 16px 24px' : '0 40px 40px',
                    flex: screens.xs ? '1 1 auto' : 'none',
                    overflow: screens.xs ? 'auto' : 'visible',
                    minHeight: 0
                }}>
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        size={screens.xs ? "small" : "large"}
                        style={{ marginTop: 24 }}
                        tabPosition={screens.xs ? "top" : "top"}
                        items={[
                            {
                                key: 'overview',
                                label: screens.xs ? 'Overview' : (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <BookOutlined />
                                        Overview
                                    </span>
                                ),
                                children: (
                                    <div style={{ padding: screens.xs ? '16px 0' : '24px 0' }}>
                                        <Title level={3} style={{
                                            color: '#2c3e50',
                                            marginBottom: 20,
                                            fontSize: screens.xs ? 18 : 24
                                        }}>
                                            Platform Description
                                        </Title>
                                        <Paragraph style={{
                                            fontSize: screens.xs ? 14 : 15,
                                            lineHeight: 1.7,
                                            marginBottom: 16
                                        }}>
                                            Ragomics addresses the computational barriers that limit researchers'
                                            ability to analyze single-cell data effectively. The platform integrates
                                            established bioinformatics workflows with an intuitive interface,
                                            enabling researchers to focus on biological interpretation rather than
                                            technical implementation.
                                        </Paragraph>
                                        <Paragraph style={{
                                            fontSize: screens.xs ? 14 : 15,
                                            lineHeight: 1.7,
                                            marginBottom: 16
                                        }}>
                                            The system supports standard single-cell analysis workflows including
                                            quality control, normalization, dimensional reduction, clustering,
                                            differential expression analysis, and pathway enrichment. All analyses
                                            are performed using peer-reviewed algorithms and best practices from
                                            the computational biology community.
                                        </Paragraph>
                                        <Paragraph style={{
                                            fontSize: screens.xs ? 14 : 15,
                                            lineHeight: 1.7,
                                            marginBottom: 24
                                        }}>
                                            An integrated AI agent system provides intelligent assistance throughout
                                            the analysis workflow, offering automated parameter optimization,
                                            error diagnosis and resolution, and contextual biological interpretation
                                            of results based on current literature and domain knowledge.
                                        </Paragraph>

                                        <Title level={4} style={{
                                            color: '#2c3e50',
                                            marginTop: 32,
                                            marginBottom: 16,
                                            fontSize: screens.xs ? 16 : 18
                                        }}>
                                            Key Objectives
                                        </Title>
                                        <ul style={{
                                            paddingLeft: 20,
                                            lineHeight: 1.8,
                                            fontSize: screens.xs ? 14 : 15
                                        }}>
                                            <li>Democratize access to advanced single-cell analysis methods</li>
                                            <li>Reduce technical barriers for biological researchers</li>
                                            <li>Ensure reproducibility through standardized workflows</li>
                                            <li>Facilitate collaboration through shared analysis environments</li>
                                            <li>Provide AI-assisted guidance for optimal analysis decisions</li>
                                            <li>Enable intelligent interpretation of complex biological data</li>
                                        </ul>
                                    </div>
                                )
                            },
                            {
                                key: 'capabilities',
                                label: screens.xs ? 'Features' : (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <SettingOutlined />
                                        Capabilities
                                    </span>
                                ),
                                children: (
                                    <div style={{ padding: screens.xs ? '16px 0' : '24px 0' }}>
                                        <Title level={3} style={{
                                            color: '#2c3e50',
                                            marginBottom: 24,
                                            fontSize: screens.xs ? 18 : 24
                                        }}>
                                            Technical Capabilities
                                        </Title>

                                        <Row gutter={[screens.xs ? 12 : 24, screens.xs ? 12 : 24]} style={{ marginBottom: 32 }}>
                                            {capabilities.map((capability, index) => (
                                                <Col xs={24} lg={12} key={index}>
                                                    <Card
                                                        style={{
                                                            height: '100%',
                                                            border: '1px solid #e8e8e8',
                                                            borderRadius: 8
                                                        }}
                                                        styles={{ body: { padding: screens.xs ? '16px' : '24px' } }}
                                                    >
                                                        <Title level={4} style={{
                                                            marginBottom: 12,
                                                            fontSize: screens.xs ? 14 : 16
                                                        }}>
                                                            {capability.category}
                                                        </Title>
                                                        <Paragraph style={{
                                                            fontSize: screens.xs ? 13 : 14,
                                                            color: '#666',
                                                            marginBottom: 16,
                                                            lineHeight: 1.6
                                                        }}>
                                                            {capability.description}
                                                        </Paragraph>
                                                        <div>
                                                            {capability.technologies.map((tech, techIndex) => (
                                                                <Tag
                                                                    key={techIndex}
                                                                    style={{
                                                                        margin: '2px 4px 2px 0',
                                                                        border: '1px solid #d9d9d9',
                                                                        borderRadius: 4,
                                                                        fontSize: screens.xs ? 10 : 12
                                                                    }}
                                                                >
                                                                    {tech}
                                                                </Tag>
                                                            ))}
                                                        </div>
                                                    </Card>
                                                </Col>
                                            ))}
                                        </Row>

                                        <Title level={4} style={{ color: '#2c3e50', marginBottom: 16, fontSize: screens.xs ? 18 : 24 }}>
                                            Analysis Workflow
                                        </Title>
                                        <Timeline
                                            items={[
                                                {
                                                    dot: <DatabaseOutlined style={{ fontSize: 16 }} />,
                                                    children: (
                                                        <div>
                                                            <Title level={5} style={{ marginBottom: 4, fontSize: screens.xs ? 14 : 16 }}>Data Import & Validation</Title>
                                                            <Text style={{ color: '#666', fontSize: screens.xs ? 13 : 14 }}>
                                                                Automated format detection and quality assessment
                                                            </Text>
                                                        </div>
                                                    )
                                                },
                                                {
                                                    dot: <SettingOutlined style={{ fontSize: 16 }} />,
                                                    children: (
                                                        <div>
                                                            <Title level={5} style={{ marginBottom: 4, fontSize: screens.xs ? 14 : 16 }}>Pipeline Configuration</Title>
                                                            <Text style={{ color: '#666', fontSize: screens.xs ? 13 : 14 }}>
                                                                Template-based or custom workflow assembly
                                                            </Text>
                                                        </div>
                                                    )
                                                },
                                                {
                                                    dot: <BulbOutlined style={{ fontSize: 16 }} />,
                                                    children: (
                                                        <div>
                                                            <Title level={5} style={{ marginBottom: 4, fontSize: screens.xs ? 14 : 16 }}>AI-Assisted Configuration</Title>
                                                            <Text style={{ color: '#666', fontSize: screens.xs ? 13 : 14 }}>
                                                                Intelligent parameter suggestion and method optimization
                                                            </Text>
                                                        </div>
                                                    )
                                                },
                                                {
                                                    dot: <ThunderboltOutlined style={{ fontSize: 16 }} />,
                                                    children: (
                                                        <div>
                                                            <Title level={5} style={{ marginBottom: 4, fontSize: screens.xs ? 14 : 16 }}>Computational Execution</Title>
                                                            <Text style={{ color: '#666', fontSize: screens.xs ? 13 : 14 }}>
                                                                Scalable processing with automated error handling
                                                            </Text>
                                                        </div>
                                                    )
                                                },
                                                {
                                                    dot: <BarChartOutlined style={{ fontSize: 16 }} />,
                                                    children: (
                                                        <div>
                                                            <Title level={5} style={{ marginBottom: 4, fontSize: screens.xs ? 14 : 16 }}>Results & AI Interpretation</Title>
                                                            <Text style={{ color: '#666', fontSize: screens.xs ? 13 : 14 }}>
                                                                Interactive visualization with contextual biological insights
                                                            </Text>
                                                        </div>
                                                    )
                                                }
                                            ]}
                                        />
                                    </div>
                                )
                            },
                            {
                                key: 'technical',
                                label: screens.xs ? 'Technical' : (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <FileTextOutlined />
                                        Technical Specifications
                                    </span>
                                ),
                                children: (
                                    <div style={{ padding: screens.xs ? '16px 0' : '24px 0' }}>
                                        <Title level={3} style={{
                                            color: '#2c3e50',
                                            marginBottom: 24,
                                            fontSize: screens.xs ? 18 : 24
                                        }}>
                                            System Architecture & Specifications
                                        </Title>

                                        <Table
                                            columns={screens.xs ? [
                                                {
                                                    title: 'Specifications',
                                                    dataIndex: 'component',
                                                    key: 'component',
                                                    render: (text, record) => (
                                                        <div>
                                                            <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 12 }}>
                                                                {record.component}
                                                            </div>
                                                            <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>
                                                                {record.specification}
                                                            </div>
                                                            <Text code style={{ fontSize: 10 }}>
                                                                {record.details}
                                                            </Text>
                                                        </div>
                                                    )
                                                }
                                            ] : columns}
                                            dataSource={technicalSpecs}
                                            pagination={false}
                                            size={screens.xs ? "small" : "middle"}
                                            style={{ marginBottom: 32 }}
                                            scroll={screens.xs ? { x: true } : undefined}
                                        />
                                    </div>
                                )
                            }
                        ]}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default AboutModal;